import { Inject, Injectable } from '@angular/core';
import {
  HttpContext,
  HttpContextToken,
  HttpEvent,
  HttpInterceptor,
  HttpHandler,
  HttpRequest,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, timeout } from 'rxjs/operators';
import { InsightsService } from '@maximizer/core/shared/insights';
import { HTTP_RETRY_AMOUNT, HTTP_TIMEOUT } from '../injection-token';
import { SeverityLevel } from '@microsoft/applicationinsights-web';
import { OAUTH2_PATHS } from '@maximizer/oauth2/data-access';

export const SKIP_TIMEOUT_RETRY = new HttpContextToken<boolean>(() => false);

export function skipTimeoutRetry(): HttpContext {
  return new HttpContext().set(SKIP_TIMEOUT_RETRY, true);
}

@Injectable()
export class TimeoutRetryInterceptor implements HttpInterceptor {
  // Extended timeout for getting mime content and creating attachment for multiple documents
  private readonly MIME_CONTENT_AND_ATTACHMENT_TIMEOUT = 60000;
  // Extended timeout for GetSessionInfo showing it can take longer in some environments
  private readonly SESSION_INFO_TIMEOUT = 30000;
  // extended timeout for AI API calls as they can take longer to respond
  private readonly AI_API_TIMEOUT = 40000;

  private readonly ATTEMPT_TIMEOUT_MULTIPLIERS = [1, 1.5, 2];
  private readonly OAUTH2_TOKEN_PATHS = [
    OAUTH2_PATHS.TOKEN,
    OAUTH2_PATHS.ON_PREM_TOKEN,
  ];

  constructor(
    @Inject(HTTP_TIMEOUT) private readonly httpTimeout: number,
    @Inject(HTTP_RETRY_AMOUNT) private readonly httpRetryAmount: number,
    private readonly insights: InsightsService,
  ) {}

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    if (this.isRefreshTokenRequest(request)) {
      return next.handle(request);
    }

    // MS OAuth codes are single-use; a retry would re-POST the code and produce duplicates.
    if (this.isMicrosoftCodeExchangeRequest(request)) {
      return next.handle(request);
    }

    return this.handleRequestWithRetry(request, next, 0);
  }

  private handleRequestWithRetry(
    request: HttpRequest<any>,
    next: HttpHandler,
    retryAttempt: number,
  ): Observable<HttpEvent<any>> {
    const baseTimeout = this.getTimeoutForRequest(request);
    const effectiveTimeout = this.getTimeoutForAttempt(
      baseTimeout,
      retryAttempt,
    );

    return next.handle(request).pipe(
      timeout(effectiveTimeout),
      catchError((error: any) => {
        if (!this.isTimeoutError(error)) {
          return throwError(() => error);
        }

        if (retryAttempt < this.httpRetryAmount && this.isRetryable(request)) {
          const nextAttempt = retryAttempt + 1;

          console.warn(
            `Retry ${nextAttempt}/${this.httpRetryAmount} for ${request.method} ${request.url} with timeout ${effectiveTimeout}ms`,
          );

          return this.handleRequestWithRetry(request, next, nextAttempt);
        }

        console.error(
          `Request timed out after ${effectiveTimeout} ms (after ${retryAttempt} retries):`,
          error,
        );
        this.trackTimeoutEvent(request, retryAttempt, effectiveTimeout);
        return throwError(
          () => new Error('Request timed out. Please try again.'),
        );
      }),
    );
  }

  private getTimeoutForRequest(request: HttpRequest<any>): number {
    if (
      request.url.includes('graph.microsoft.com/v1.0') ||
      request.url.includes('/AttachmentCreate')
    ) {
      return this.MIME_CONTENT_AND_ATTACHMENT_TIMEOUT;
    }

    if (request.url.includes('/json/GetSessionInfo')) {
      return this.SESSION_INFO_TIMEOUT;
    }

    if (
      request.url.includes('/ai/v1') ||
      request.url.includes('/integration/maximizer-ai/v1')
    ) {
      return this.AI_API_TIMEOUT;
    }

    return this.httpTimeout;
  }

  private trackTimeoutEvent(
    request: HttpRequest<any>,
    retriesAttempted: number,
    timeoutUsed: number,
  ): void {
    this.insights.trackEvent(
      { name: 'HttpRequestTimeout' },
      SeverityLevel.Error,
      {
        url: request.url,
        method: request.method,
        timeout: timeoutUsed,
        retriesAttempted,
      },
    );
  }

  private isRetryable(request: HttpRequest<any>): boolean {
    return !request.context.get(SKIP_TIMEOUT_RETRY);
  }

  private isTimeoutError(error: any): boolean {
    if (error?.name === 'TimeoutError') return true;
    if (error?.message?.toLowerCase().includes('timeout')) return true;
    if (error?.code === 'TIMEOUT') return true;
    return false;
  }

  private getTimeoutForAttempt(
    baseTimeout: number,
    retryAttempt: number,
  ): number {
    const multiplier =
      this.ATTEMPT_TIMEOUT_MULTIPLIERS[retryAttempt] ??
      this.ATTEMPT_TIMEOUT_MULTIPLIERS.at(-1);

    return Math.round(baseTimeout * multiplier);
  }

  private isRefreshTokenRequest(request: HttpRequest<any>): boolean {
    if (request.method !== 'POST') {
      return false;
    }

    return this.OAUTH2_TOKEN_PATHS.some((path) =>
      request.url.toLowerCase().includes(path.toLowerCase()),
    );
  }

  private isMicrosoftCodeExchangeRequest(request: HttpRequest<any>): boolean {
    if (request.method !== 'POST') {
      return false;
    }
    const url = request.url.toLowerCase();
    return (
      url.includes('/microsoft-auth/exchange-code') ||
      (url.includes('/microsoft-auth/sync-configurations/') &&
        url.endsWith('/exchange-code'))
    );
  }
}
